import { defineStore } from "pinia"
import { ref } from "vue"

import axios from "@axios"
import { reportError } from "@/plugins/misc"

const baseUrl = '/emailtemplate/'

/**
 * Unescape HTML entities introduced by the visual editor (GrapeJS / TipTap).
 * Their DOM serializers escape < and > to &lt; and &gt; which breaks
 * underscore template syntax and turns characters like => into =&gt;.
 * The > character never needs escaping in HTML, so a blanket unescape is safe.
 */
function unescapeTemplateTags(content) {
  if (!content) return content

  return content

    // Fix double-encoded entities from repeated save cycles (e.g. =&amp;gt; → =&gt;)
    .replace(/&amp;gt;/g, '&gt;')
    .replace(/&amp;lt;/g, '&lt;')

    // Unescape > globally — safe because > never needs escaping in HTML
    .replace(/&gt;/g, '>')

    // Unescape <%= %> and <% %> template tags
    .replace(/&lt;%=/g, '<%=')
    .replace(/&lt;%-/g, '<%-')
    .replace(/&lt;%/g, '<%')
}

export const useEmailTemplatesStore = defineStore('emailTemplatesStore', () => {
  // State
  const template = ref({
    _id: '',
    templateName: '',
    displayName: '',
    description: '',
    content: '',
    dataFieldGroup: 'system',
    version: 1,
    isActive: true,
    defaultSubject: '',
    defaultPreheader: '',
  })

  const templates = ref([])
  const versions = ref([])
  const fieldGroups = ref({})
  const templateMapping = ref({})
  const allowedSenders = ref([])
  const defaultSenderKey = ref('preferred')
  const loading = ref(false)

  // Initialize functions
  function initTemplates(data) {
    templates.value = data
  }

  function initTemplate(data) {
    template.value = data
  }

  function initVersions(data) {
    versions.value = data
  }

  function initFieldGroups(data) {
    fieldGroups.value = data.fieldGroups || {}
    templateMapping.value = data.templateMapping || {}
  }

  // API Functions

  /**
   * Get all email templates (list view, without full content)
   */
  async function getTemplates(includeInactive = false) {
    try {
      loading.value = true
      const res = await axios.get(baseUrl, {
        params: {
          includeInactive: includeInactive.toString(),
        }
      })

      if (!res.data) {
        console.log("No templates found")
        
        return []
      }

      initTemplates(res.data)
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:getTemplates", error.message, null, null, null, error)
      
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * Get a single template by ID (with full content)
   */
  async function getTemplateById(id) {
    try {
      loading.value = true
      const res = await axios.get(`${baseUrl}${id}`, {
        params: {
        }
      })

      if (!res.data) {
        console.log("No template found")
        
        return null
      }

      initTemplate(res.data)
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:getTemplateById", error.message, null, null, null, error)
      
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Get a template by name
   */
  async function getTemplateByName(templateName) {
    try {
      loading.value = true
      const res = await axios.get(`${baseUrl}name/${templateName}`, {
        params: {
        }
      })

      if (!res.data) {
        console.log("No template found")
        
        return null
      }

      initTemplate(res.data)
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:getTemplateByName", error.message, null, null, null, error)
      
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Create a new template
   */
  async function createTemplate(data) {
    try {
      loading.value = true

      // Unescape template tags that TipTap may have escaped
      const processedData = {
        ...data,
        content: unescapeTemplateTags(data.content),
      }
      const res = await axios.post(baseUrl, processedData, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:createTemplate", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Update a template (creates new version)
   */
  async function updateTemplate(id, data) {
    try {
      loading.value = true

      // Unescape template tags that TipTap may have escaped
      const processedData = {
        ...data,
        content: unescapeTemplateTags(data.content),
      }
      const res = await axios.put(`${baseUrl}${id}`, processedData, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:updateTemplate", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Delete a template (soft delete)
   */
  async function deleteTemplate(id) {
    try {
      loading.value = true
      const res = await axios.delete(`${baseUrl}${id}`, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:deleteTemplate", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Restore a soft-deleted template
   */
  async function restoreTemplate(id) {
    try {
      loading.value = true
      const res = await axios.put(`${baseUrl}${id}/restore`, {}, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:restoreTemplate", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Clone/copy a template
   */
  async function cloneTemplate(id) {
    try {
      loading.value = true
      const res = await axios.post(`${baseUrl}${id}/clone`, {}, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:cloneTemplate", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Get version history for a template
   */
  async function getVersions(templateId) {
    try {
      loading.value = true
      const res = await axios.get(`${baseUrl}${templateId}/versions`, {
        params: {
        }
      })

      initVersions(res.data || [])
      
      return res.data || []
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:getVersions", error.message, null, null, null, error)
      
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * Get a specific version
   */
  async function getVersion(templateId, version) {
    try {
      loading.value = true
      const res = await axios.get(`${baseUrl}${templateId}/versions/${version}`, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:getVersion", error.message, null, null, null, error)
      
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Rollback to a specific version
   */
  async function rollbackToVersion(templateId, version) {
    try {
      loading.value = true
      const res = await axios.post(`${baseUrl}${templateId}/rollback/${version}`, {}, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:rollbackToVersion", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Generate preview HTML for a template
   */
  async function previewTemplate(templateId, sampleData = {}, preheader = '') {
    try {
      loading.value = true
      const res = await axios.post(`${baseUrl}${templateId}/preview`, {
        sampleData,
        preheader,
      }, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:previewTemplate", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Generate preview HTML from raw content (before saving)
   */
  async function previewContent(content, dataFieldGroup, sampleData = {}, preheader = '') {
    try {
      loading.value = true

      // Unescape template tags that TipTap may have escaped
      const processedContent = unescapeTemplateTags(content)
      const res = await axios.post(`${baseUrl}preview-content`, {
        content: processedContent,
        dataFieldGroup,
        sampleData,
        preheader,
      }, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:previewContent", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Send a test email
   */
  async function sendTestEmail(templateId, sampleData = {}, subject = '', preheader = '') {
    try {
      loading.value = true
      const res = await axios.post(`${baseUrl}${templateId}/test-email`, {
        sampleData,
        subject,
        preheader,
      }, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:sendTestEmail", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Get all field groups and template mapping
   */
  async function getFieldGroups() {
    try {
      const res = await axios.get(`${baseUrl}field-groups`, {
        params: {
        }
      })

      initFieldGroups(res.data || {})
      
      return res.data || {}
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:getFieldGroups", error.message, null, null, null, error)
      
      return { fieldGroups: {}, templateMapping: {} }
    }
  }

  /**
   * Load the allowed sender allowlist (from / cc / bcc).
   * Cached on the store — only fetched on first access.
   */
  async function getAllowedSenders() {
    if (allowedSenders.value.length > 0) {
      return { senders: allowedSenders.value, defaultSenderKey: defaultSenderKey.value }
    }
    try {
      const res = await axios.get(`${baseUrl}allowed-senders`, {
        params: {
        },
      })
      allowedSenders.value = res.data?.senders || []
      defaultSenderKey.value = res.data?.defaultSenderKey || 'preferred'

      return res.data || { senders: [], defaultSenderKey: 'preferred' }
    } catch (error) {
      console.error(error)
      reportError('emailTemplates.store.js:getAllowedSenders', error.message, null, null, null, error)

      return { senders: [], defaultSenderKey: 'preferred' }
    }
  }

  /**
   * Seed templates from file system (migration helper)
   */
  async function seedTemplates() {
    try {
      loading.value = true
      const res = await axios.post(`${baseUrl}seed`, {}, {
        params: {
        }
      })
      
      return res.data
    } catch (error) {
      console.error(error)
      reportError("emailTemplates.store.js:seedTemplates", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    template,
    templates,
    versions,
    fieldGroups,
    templateMapping,
    allowedSenders,
    defaultSenderKey,
    loading,

    // Init functions
    initTemplates,
    initTemplate,
    initVersions,
    initFieldGroups,

    // API functions
    getTemplates,
    getTemplateById,
    getTemplateByName,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    restoreTemplate,
    cloneTemplate,
    getVersions,
    getVersion,
    rollbackToVersion,
    previewTemplate,
    previewContent,
    sendTestEmail,
    getFieldGroups,
    getAllowedSenders,
    seedTemplates,
  }
})
